import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Send, User, Bot } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiPost } from '../../utils/api';
import { Button, Card, Input, Spinner, Badge, ChatBubble, ChatInput, ChatContainer, MessageList } from '../design-system';

// Task-specific configuration moved from taskTypes.js
export const OnboardingTaskConfig = {
  systemPrompt: `You are an AI assistant helping with patient onboarding for TheraEngage.

Your role:
1. Collect basic patient information (name, age, contact info)
2. Gather medical history and current medications
3. Understand the reason for seeking therapy
4. Assess current symptoms and concerns
5. Collect family and social history
6. Discuss therapeutic goals and expectations

Guidelines:
- Be warm, professional, and empathetic
- Ask one question at a time
- Use the patient's responses to guide next questions
- Validate their feelings and experiences
- Keep the conversation natural and flowing
- Summarize collected information at the end

Current patient: {{USER_NAME}}

Start by introducing yourself and explaining the onboarding process.`,
  completionCriteria: ["basic_info_collected", "medical_history_gathered", "therapy_goals_discussed"]
};

const OnboardingTask = forwardRef(({ task, taskType, session, onComplete }, ref) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const messagesEndRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Expose downloadConversation function to parent component
  useImperativeHandle(ref, () => ({
    downloadConversation
  }));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!session || hasInitializedRef.current) {
      return;
    }
    
    hasInitializedRef.current = true;
    
    // Load existing chat history if session exists and has messages
    if (session.chatHistory && session.chatHistory.length > 0) {
      // Convert old format to new format if needed
      const convertedMessages = session.chatHistory.map(msg => ({
        role: msg.role || (msg.type === 'user' ? 'user' : 'assistant'),
        content: msg.content,
        timestamp: msg.timestamp
      }));
      setMessages(convertedMessages);
    } else {
      // Send empty message to get initial greeting from LLM
      sendInitialGreeting();
    }
  }, [session]);

  const downloadConversation = () => {
    if (messages.length === 0) {
      toast.error('No conversation to download');
      return;
    }

    // Format conversation for download
    const conversationText = messages.map((message, index) => {
      const timestamp = new Date(message.timestamp).toLocaleString();
      const role = message.role === 'user' ? 'You' : 'Therapist Assistant';
      return `${timestamp} - ${role}:\n${message.content}\n`;
    }).join('\n---\n\n');

    const fullText = `Onboarding Conversation\n${'='.repeat(50)}\n\n${conversationText}`;

    // Create and download file
    const blob = new Blob([fullText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `onboarding-conversation-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Conversation downloaded successfully!');
  };

  const sendInitialGreeting = async () => {
    if (isLoading) {
      return;
    }
    
    setIsLoading(true);
    try {
      const data = await apiPost('/.netlify/functions/onboardingTaskApi', {
        sessionId: session.sessionId,
        message: 'start'
      });
      
      const aiMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages([aiMessage]);

      // Check if task is completed (unlikely on start, but just in case)
      if (data.isCompleted) {
        setIsCompleted(true);
        toast.success('Onboarding completed! You can now finish the task.');
      }
    } catch (error) {
      console.error('Error getting initial greeting:', error);
      toast.error('Failed to get initial greeting. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessageToAPI = async (message) => {
    try {
      const data = await apiPost('/.netlify/functions/onboardingTaskApi', {
        sessionId: session.sessionId,
        message: message
      });
      
      const aiMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);

      // Check if task is completed
      if (data.isCompleted) {
        setIsCompleted(true);
        toast.success('Onboarding completed! You can now finish the task.');
        
        // Automatically complete the task session
        try {
          const completeResult = await apiPost('/.netlify/functions/complete-task-session', {
            sessionId: session.sessionId,
            taskId: task.taskId
          });
          if (completeResult) {
            toast.success('Task marked as completed!');
          }
        } catch (completeError) {
          console.error('Error completing task:', completeError);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    await sendMessageToAPI(inputMessage.trim());
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleComplete = async () => {
    try {
      const result = await apiPost('/.netlify/functions/complete-task-session', {
        sessionId: session.sessionId,
        taskId: task.taskId
      });
      if (result) {
        toast.success('Onboarding task completed successfully!');
        onComplete();
      } else {
        throw new Error('Failed to complete task');
      }
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };



  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* Chat Messages - Scrollable area */}
      <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-4 min-h-0 w-full">
        <MessageList 
          messages={messages}
          isLoading={isLoading}
          loadingMessage="AI is thinking..."
          showTimestamps={true}
          showStatus={true}
          showReasoning={true}
        />
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input - Fixed at bottom */}
      {!isCompleted ? (
        <div className="border-t border-gray-200 p-3 md:p-4 bg-white flex-shrink-0">
          <ChatInput
            onSendMessage={(message) => {
              // Create user message and add to messages
              const userMessage = {
                role: 'user',
                content: message,
                timestamp: new Date()
              };
              setMessages(prev => [...prev, userMessage]);
              setInputMessage('');
              setIsLoading(true);

              // Send message to API
              sendMessageToAPI(message);
            }}
            onTyping={() => {}}
            disabled={isLoading}
            placeholder="Type your message..."
            showFileUpload={false}
            showEmoji={false}
          />
        </div>
      ) : (
        <div className="border-t border-gray-200 p-3 md:p-4 bg-white flex-shrink-0">
          <Card variant="default" padding="medium" className="text-center">
            <p className="text-green-600 font-medium mb-4 text-sm md:text-base">
              ✅ Onboarding completed! You've provided all the necessary information.
            </p>
            <Button
              variant="success"
              size="md"
              onClick={handleComplete}
              className="px-4 md:px-6 py-2"
            >
              Complete Task
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
});

// Add static methods to the component for Summary and ResultsSummary
OnboardingTask.Summary = ({ task, session }) => {
  return null;
};

OnboardingTask.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData) return null;

  return (
    <div className="space-y-4">
      <Card variant="default" padding="medium" className="bg-gray-50">
        <h4 className="text-lg font-semibold text-gray-900 mb-3">Onboarding Summary - {patientName}</h4>
        
        <div className="space-y-4">
          {completionData.therapyReason && (
            <Card variant="default" padding="small" className="bg-white">
              <h6 className="text-sm font-medium text-gray-700 mb-2">Reason for Therapy:</h6>
              <p className="text-sm text-gray-600">{completionData.therapyReason}</p>
            </Card>
          )}
          
          {completionData.currentSymptoms && completionData.currentSymptoms.length > 0 && (
            <Card variant="default" padding="small" className="bg-white">
              <h6 className="text-sm font-medium text-gray-700 mb-2">Current Symptoms:</h6>
              <ul className="text-sm text-gray-600 space-y-1">
                {completionData.currentSymptoms.map((symptom, index) => (
                  <li key={index} className="flex items-start">
                    <Badge variant="error" size="sm" className="mr-2">•</Badge>
                    <span>{symptom}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          
          {completionData.therapyGoals && completionData.therapyGoals.length > 0 && (
            <Card variant="default" padding="small" className="bg-white">
              <h6 className="text-sm font-medium text-gray-700 mb-2">Therapy Goals:</h6>
              <ul className="text-sm text-gray-600 space-y-1">
                {completionData.therapyGoals.map((goal, index) => (
                  <li key={index} className="flex items-start">
                    <Badge variant="success" size="sm" className="mr-2">•</Badge>
                    <span>{goal}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          
          {completionData.medicalHistory && (
            <Card variant="default" padding="small" className="bg-white">
              <h6 className="text-sm font-medium text-gray-700 mb-2">Medical History:</h6>
              <p className="text-sm text-gray-600">{completionData.medicalHistory}</p>
            </Card>
          )}
          
          {completionData.familyHistory && (
            <Card variant="default" padding="small" className="bg-white">
              <h6 className="text-sm font-medium text-gray-700 mb-2">Family History:</h6>
              <p className="text-sm text-gray-600">{completionData.familyHistory}</p>
            </Card>
          )}
        </div>
      </Card>
    </div>
  );
};

// Add static method for task assignment form
// OnboardingTask.AssignTaskForm = undefined; // No custom form needed

export default OnboardingTask;